// App.tsx
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from "./core/Login";
import { AccountsOverview } from "./accounts/AccountsOverview";
import { AuthProvider } from "./core/AuthProvider";
import PrivateRoute from "./core/PrivateRoute";
import AdminRoute from "./core/AdminRoute";
import ModuleRoute from "./core/ModuleRoute";
import Layout from "./core/Layout";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DrinkOverview } from "./drinks/DrinkOverview";
import { ThemeProvider } from "@mui/material";
import '@mui/x-data-grid/themeAugmentation';
import { ThemeModeProvider, useThemeMode } from './core/ThemeContext';
import Calendar from "./calendar/Calendar";
import Dashboard from "./dashboard/Dashboard";
import CleaningFeesOverview from "./cleaningFees/CleaningFeesOverview";
import ResetPassword from "./core/ResetPassword";
import EmailResetForm from "./core/EmailResetForm";
import EmailChangeConfirm from "./core/EmailChangeConfirm";
import ReservationOverview from "./reservations/ReservationOverview";
import { SystemMailOverview } from './mailing/systemmails/SystemMailOverview';
import { AlertProvider } from './core/AlertContext';
import ReservationPriceOverview from "./ReservationPrice/ReservationPriceOverview";
import { GlobalMail } from "./mailing/globalmails/GlobalMail";
import StoragePage from "./storage/StoragePage";
import SettingsPage from "./settings/SettingsPage";
import InfoPage from './info/InfoPage';
import BlogListPage from './blog/pages/BlogListPage';
import BlogDetailPage from './blog/pages/BlogDetailPage';
import BlogEditorPage from './blog/pages/BlogEditorPage';
import RoomOverview from './rooms/RoomOverview';
import HutBookOverview from './hutBook/HutBookOverview';
import ActivityLogPage from './activity/ActivityLogPage';
import NotificationsPage from './notifications/NotificationsPage';
import SearchPage from './search/SearchPage';
import MeetingPage from './meetings/MeetingPage';

const queryClient = new QueryClient();

function AppInner() {
    const { theme } = useThemeMode();
    useEffect(() => {
        fetch('/api/csrf/').then(r => r.json()).catch(err => console.error('CSRF fetch error:', err));
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
                <AlertProvider>
                    <BrowserRouter>
                        <Layout>
                            <Routes>
                                <Route path="/" element={<Login />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/reset-password" element={<ResetPassword />} />
                                <Route path="/forgot-password" element={<EmailResetForm />} />
                                <Route path="/email-bestaetigen" element={<EmailChangeConfirm />} />
                                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                                <Route path="/storage" element={<ModuleRoute module="storage"><StoragePage /></ModuleRoute>} />
                                <Route path="/activity-log" element={<AdminRoute><ActivityLogPage /></AdminRoute>} />
                                <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
                                <Route path="/search" element={<PrivateRoute><SearchPage /></PrivateRoute>} />
                                <Route path="/meeting" element={<ModuleRoute module="meetings"><MeetingPage /></ModuleRoute>} />
                                <Route path="/einstellungen" element={<AdminRoute><SettingsPage /></AdminRoute>} />
                                <Route path="/infos" element={<ModuleRoute module="info"><InfoPage /></ModuleRoute>} />
                                <Route path="/blog" element={<ModuleRoute module="blog"><BlogListPage /></ModuleRoute>} />
                                <Route path="/blog/new" element={<ModuleRoute module="blog"><BlogEditorPage /></ModuleRoute>} />
                                <Route path="/blog/:id" element={<ModuleRoute module="blog"><BlogDetailPage /></ModuleRoute>} />
                                <Route path="/blog/:id/edit" element={<ModuleRoute module="blog"><BlogEditorPage /></ModuleRoute>} />
                                <Route path="/accounts" element={<PrivateRoute><AccountsOverview /></PrivateRoute>} />
                                <Route path="/accounts/:id" element={<PrivateRoute><AccountsOverview /></PrivateRoute>} />
                                <Route path="/drinks" element={<PrivateRoute><DrinkOverview /></PrivateRoute>} />
                                <Route path="/events" element={<PrivateRoute><Calendar calendarType="event" /></PrivateRoute>} />
                                <Route path="/events/:id" element={<PrivateRoute><Calendar calendarType="event" /></PrivateRoute>} />
                                <Route path="/bookings" element={<PrivateRoute><Calendar calendarType="booking" /></PrivateRoute>} />
                                <Route path="/bookings/:id" element={<PrivateRoute><Calendar calendarType="booking" /></PrivateRoute>} />
                                <Route path="/cleaningfee" element={<PrivateRoute><CleaningFeesOverview /></PrivateRoute>} />
                                <Route path="/rooms" element={<PrivateRoute><RoomOverview /></PrivateRoute>} />
                                <Route path="/myreservations" element={<PrivateRoute><ReservationOverview /></PrivateRoute>} />
                                <Route path="/myreservations/:id" element={<PrivateRoute><ReservationOverview /></PrivateRoute>} />
                                <Route path="/huttenbuch" element={<ModuleRoute module="hutbook"><HutBookOverview /></ModuleRoute>} />
                                <Route path="/reservationprice" element={<PrivateRoute><ReservationPriceOverview /></PrivateRoute>} />
                                <Route path="/mail" element={<PrivateRoute><GlobalMail /></PrivateRoute>} />
                                <Route path="/system_mails" element={<PrivateRoute><SystemMailOverview /></PrivateRoute>} />
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </Layout>
                    </BrowserRouter>
                </AlertProvider>
            </QueryClientProvider>
        </ThemeProvider>
    );
}

function App() {
    return (
        <AuthProvider>
            <ThemeModeProvider>
                <AppInner />
            </ThemeModeProvider>
        </AuthProvider>
    );
}

export default App;
